import { Checkbox, Chip, CopyElement, Hint, SwitchButton, proxyNumber } from "@ui";
import cn from "classnames";
import style from "./TableRow.module.scss";
import { useState } from "react";
import { AbsoluteBlock } from "UI/shared";
import { ProxySettings } from "../../../ProxySettings/ProxySettings";
import { VodafoneSvg, LifecellSvg, KyivstarSvg } from "pages/user/MyProxiesPage/svg";
import { ArrowSvg } from "UI/Pagination/Arrow.svg";
import { IProxy } from "@interfaces/user/proxy";
import { AutoContinueCell, Tags } from "./components";

export const countries: Record<string, string> = {
    UA: "Ukraine"
}

export function TableRow({
    data
}: {
    data: IProxy
}) {
    const [showDetails, setShowDetails] = useState<boolean>(true);

    const expiration_date = data.end_date ? getTimeDifference(data.end_date) : "Ми готуємо ваше проксі";

    return (
        <div className={style.row}>
            <div className={cn(style.cell, "!h-fit")}>
                <div className="w-full min-w-0">
                    <div className={style.operator}>
                        {
                            data.operator.toLocaleLowerCase().split(",")[0] === "vodafone"
                                ? <VodafoneSvg />
                                : data.operator.toLocaleLowerCase().split(",")[0] === "lifecell"
                                    ? <LifecellSvg />
                                    : data.operator.toLocaleLowerCase().split(",")[0] === "kyivstar"
                                        ? <KyivstarSvg />
                                        : null
                        }
                        <p className={style.operatorName}>{data.operator}, {countries[data.tz]}</p>
                        <CopyElement
                            element={
                                <p className={style.routerId}>№{proxyNumber(data)}</p>
                            }
                            copyText={proxyNumber(data)}
                        />
                        <div
                            className="cursor-pointer select-none"
                            onClick={() => {
                                setShowDetails(state => !state)
                            }}
                        >
                            <ArrowSvg className={showDetails ? "rotate-[270deg]" : "rotate-[90deg]"} />
                        </div>
                    </div>
                    <Tags data={data}/>
                    <div className={cn(style.details, showDetails ? "h-fit pt-[15px]" : "h-0")}>
                        <div className="mb-[10px]">
                            <p className={style.label}>Socks:</p>
                            <CopyElement
                                element={
                                    <p className={style.value}>{data.socks}</p>
                                }
                                copyText={String(data.socks)}
                            />
                        </div>
                        <div className="mb-[10px]">
                            <p className={style.label}>http:</p>
                            <CopyElement
                                element={
                                    <p className={style.value}>{data.http}</p>
                                }
                                copyText={String(data.http)}
                            />
                        </div>
                        <div className="mb-[10px]">
                            <p className={style.label}>Посилання заміни IP:</p>
                            <CopyElement
                                element={
                                    <p className={style.value}>{data.change_ip_link}</p>
                                }
                                copyText={data.change_ip_link}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={style.cell}>
                <div
                    className={cn(
                        "rounded-full text-white py-[8px] px-[20px] text-[13px] leading-[13px] font-semibold",
                        data.tariff_name === "Silver" && "bg-[#aab3d3]",
                        data.tariff_name === "Platinum" && "bg-[#c6b164]",
                        data.tariff_name === "Brilliant" && "bg-[#1E9EEE]"
                    )}
                >{data.tariff_name}</div>
            </div>
            <div className={style.cell}>
                {
                    typeof expiration_date === "string"
                        ? <Chip
                            color={"green"}
                            size="middle"
                            text={expiration_date}
                        />
                        : <Hint
                            targetElement={
                                <Chip
                                    color={!expiration_date || expiration_date?.days < 3 ? "red" : "green"}
                                    size="middle"
                                    text={expiration_date ? expiration_date.days + " д, " + expiration_date.hours + " год, " + expiration_date.minutes + " хв " : "Прострочено"}
                                />
                            }
                            hintText={
                                <>
                                    Після закінчення терміну дії, проксі необхідно буде <br />
                                    знову <span className="font-bold">активувати</span> його. Поповнюйте баланс для <br />
                                    автопродовження проксі.
                                </>
                            }
                        />
                }
            </div>
            <AutoContinueCell data={data} />
            <div className={cn(style.cell, "justify-self-center")}>
                <AbsoluteBlock
                    XAlign="left"
                    advancedAdaptive
                    targetElement={
                        <div className="cursor-pointer p-[10px]">
                            <svg xmlns="http://www.w3.org/2000/svg" width="21" height="5" viewBox="0 0 21 5" fill="none">
                                <rect width="5" height="5" rx="2.5" fill="#A2AFD0" />
                                <rect x="8" width="5" height="5" rx="2.5" fill="#A2AFD0" />
                                <rect x="16" width="5" height="5" rx="2.5" fill="#A2AFD0" />
                            </svg>
                        </div>
                    }
                    content={(isOpen) => <ProxySettings isOpen={isOpen} changingPassword operator changingIP tariffExtension data={data} />}
                />
            </div>
        </div>
    )
}

function getTimeDifference(targetDateStr: string) {
    const targetDate = new Date(targetDateStr);
    const now = new Date();

    const differenceInMs = targetDate.getTime() - now.getTime();

    if (differenceInMs <= 0) {
        return null;
    }

    const days = Math.floor(differenceInMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((differenceInMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((differenceInMs % (1000 * 60 * 60)) / (1000 * 60));

    return {
        days,
        hours,
        minutes
    };
}
